/**
 * useChat Hook
 * 
 * REQUIREMENTS SUMMARY:
 * 1. Manages chat state, message sending, and conversation history for AI assistant
 * 2. Supports STREAMING responses for dramatically improved perceived speed
 * 3. Falls back to non-streaming for cached responses
 * 4. Handles tool execution status updates during streaming
 * 5. Supports mobile WebView via mobileAuthToken/mobileUserId fallback props
 *    (bypasses AuthContext session when it hasn't synced yet in WebView)
 * 
 * MOBILE WEBVIEW NOTE: In the mobile WebView, AuthContext.session may be null when
 * useChat initializes because setSession() hasn't propagated yet. The mobileAuthToken
 * and mobileUserId props (passed from MobileChatClient) provide a direct fallback so
 * chat works immediately without waiting for the full context chain.
 * 
 * PERFORMANCE: Streaming shows first token in ~500ms vs 3-8s wait for full response
 */

import type { AssistantAttachment } from '@/utils/assistant/attachmentTypes';
import { prepareScreenshot } from '@/utils/assistant/prepareScreenshot';
import { useState, useCallback, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { useOrganization } from '@/context/OrganizationContext';
import { supabase } from '@/utils/supabase/client';
import { createLogger } from '@/utils/logger/devLogger';
import { apiErrorCopy } from '@cheervue/api-error-copy';
import { scrollToAssistantAnchor } from '@/utils/assistant/anchorSpotlight';
import {
  readRememberedOrganization,
  forgetRememberedOrganization,
  REMEMBERED_ALL_ORGS_KEY,
} from '@/utils/organizations/rememberedOrganization';

/** Shown when the stream closes or times out before any answer text arrived. */
export const STREAM_TIMEOUT_MESSAGE = 'The assistant took too long. Please try again.';

/**
 * Shown when a message is sent and no gym could be resolved. `sendMessage`
 * renders a thrown message straight into the chat panel, and this path used to
 * throw the developer string "Organization ID is required".
 *
 * Deliberately says nothing about what happens next. The same throw covers a
 * gym that has not loaded YET and a person who has no gym at all
 * (`organizations.length === 0`, and the fetch-failure branch beside it), so
 * "try again in a moment" would be a promise that is false for two of the three
 * ways to get here. Computed, not predicted: it states only what was attempted.
 */
export const ORGANIZATION_NOT_READY_MESSAGE = "We couldn't tell which gym to use.";

const logger = createLogger('useChat');

// Get access token from session
function getAccessToken(session: any): string | null {
  return session?.access_token || null;
}

/**
 * Get a fresh access token by asking Supabase to refresh if needed.
 * This prevents stale tokens after idle periods — supabase.auth.getSession()
 * auto-refreshes expired JWTs before returning.
 */
async function getFreshAccessToken(mobileAuthToken?: string | null): Promise<string | null> {
  // Mobile WebView tokens are managed externally
  if (mobileAuthToken) return mobileAuthToken;

  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      logger.warn('Failed to get fresh session:', error?.message || 'No session');
      return null;
    }
    return session.access_token;
  } catch (err) {
    logger.error('Error refreshing access token:', err);
    return null;
  }
}

export interface SupportAttachmentStatus {
  request_id: string;
  retry_attachment_ids?: string[];
  detail_path: string;
  attached_count: number;
  failed: { id: string; name: string }[];
  error?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  supportAttachmentStatus?: SupportAttachmentStatus;
  attachments?: AssistantAttachment[];
  toolsUsed?: string[];
  isLoading?: boolean;
  analyticsId?: string; // ID of the chatbot_analytics record for feedback
  feedback?: 'positive' | 'negative'; // User's feedback on this message
  pendingNavigation?: { path: string; reason?: string; anchor?: string }; // Pending navigation request (#2171: anchor = section to spotlight)
}

export interface Conversation {
  id: string;
  title: string;
  user_role: string;
  message_count: number;
  last_message_at: string;
  created_at: string;
  is_active: boolean;
  messages?: ChatMessage[];
}

interface UseChatOptions {
  autoLoadHistory?: boolean;
  /** Mobile WebView: Access token passed from native app URL params. Used when AuthContext session
   *  hasn't synced yet (race condition in WebView). Enables chat to work before cookies propagate. */
  mobileAuthToken?: string | null;
  /** Mobile WebView: User ID from server-verified token. Used with mobileAuthToken for API calls. */
  mobileUserId?: string;
}

interface UseChatReturn {
  // State
  messages: ChatMessage[];
  conversations: Conversation[];
  currentConversationId: string | null;
  isOpen: boolean;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  
  // Actions
  sendMessage: (message: string, quickCommand?: string, files?: File[]) => Promise<boolean>;
  draftKey: number;
  /** Text waiting to be put in the input box, not sent (openChatAssistant `draft`). */
  draft: ChatDraft | null;
  /** Put text in the input box without sending it. */
  prefillDraft: (text: string) => void;
  /** The draft was sent or edited; forget it so a reset or reopen does not put it back. */
  clearDraft: (id: number) => void;
  loadAttachment: (id: string) => Promise<Blob>;
  retrySupportAttachments: (messageId: string) => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (conversationId: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  setIsOpen: (open: boolean) => void;
  toggleOpen: () => void;
  clearError: () => void;
  /** Stop the answer that is streaming right now. Keeps any text already shown. */
  stopGeneration: () => void;
  refreshConversations: () => Promise<void>;
  refreshSuggestions: () => Promise<void>;
  suggestions: string[];
  submitFeedback: (messageId: string, feedback: 'positive' | 'negative', comment?: string) => Promise<void>;
  confirmNavigation: (messageId: string, confirm: boolean) => void;
}

/** Text for the input box. `id` changes on every prefill, so the same words twice still land. */
export interface ChatDraft {
  id: number;
  text: string;
}

export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const { autoLoadHistory = true, mobileAuthToken, mobileUserId } = options;
  
  const { user, session } = useAuth();
  // Effective user ID: context or mobile override (for WebView before AuthContext syncs)
  const effectiveUserId = user?.id || mobileUserId || '';
  // Effective access token: session or mobile override (for WebView before AuthContext syncs)
  const effectiveAccessToken = getAccessToken(session) || mobileAuthToken || null;
  const { currentOrganization, isLoading: isOrganizationLoading } = useOrganization();
  const router = useRouter();
  
  // Get current page path - check if we're in mobile WebView first
  const webPathname = usePathname(); // Web app pathname
  const isMobileWebView = typeof window !== 'undefined' && (window as any).__isCheerVueMobile;
  
  // State to track mobile page changes
  const [mobilePage, setMobilePage] = useState<string>(() => {
    if (typeof window !== 'undefined' && isMobileWebView) {
      return localStorage.getItem('mobileCurrentPage') || '';
    }
    return '';
  });
  
  // Listen for mobile page changes from native app
  useEffect(() => {
    if (!isMobileWebView) return;
    
    const handlePageChange = (event: CustomEvent) => {
      const newPath = event.detail?.path || '';
      setMobilePage(newPath);
      if (typeof window !== 'undefined') {
        localStorage.setItem('mobileCurrentPage', newPath);
      }
      logger.debug('Mobile page changed in useChat:', newPath);
    };
    
    // Also check localStorage periodically for updates (fallback)
    const checkPageInterval = setInterval(() => {
      if (typeof window !== 'undefined') {
        const storedPage = localStorage.getItem('mobileCurrentPage') || '';
        if (storedPage !== mobilePage) {
          setMobilePage(storedPage);
        }
      }
    }, 1000); // Check every second
    
    if (typeof window !== 'undefined') {
      window.addEventListener('mobilePageChanged', handlePageChange as EventListener);
    }
    
    return () => {
      clearInterval(checkPageInterval);
      if (typeof window !== 'undefined') {
        window.removeEventListener('mobilePageChanged', handlePageChange as EventListener);
      }
    };
  }, [isMobileWebView, mobilePage]);
  
  // Use mobile page path if in WebView, otherwise use web pathname
  const pathname = isMobileWebView && mobilePage ? mobilePage : (webPathname || '');
  
  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('chat_current_conversation_id');
    }
    return null;
  });
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('chat_is_open') === 'true';
    }
    return false;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // The in-flight request, so the Stop button can abort it; and whether the
  // abort was the person's choice (no error line) or a timeout (error line).
  const inFlightAbortRef = useRef<AbortController | null>(null);
  const sendLockRef = useRef(false);
  const generationRef = useRef(0);
  const [draftKey, setDraftKey] = useState(0);
  const [draft, setDraft] = useState<ChatDraft | null>(null);
  const prefillDraft = useCallback((text: string) => {
    setDraft((previous) => ({ id: (previous?.id ?? 0) + 1, text }));
  }, []);
  const clearDraft = useCallback((id: number) => {
    setDraft((previous) => (previous?.id === id ? null : previous));
  }, []);
  const uploadedRef = useRef(new Map<File, { attachment: AssistantAttachment; conversationId: string }>());
  const resetDraft = useCallback(() => {
    generationRef.current += 1;
    inFlightAbortRef.current?.abort();
    uploadedRef.current.clear();
    setIsLoading(false);
    setDraftKey(value => value + 1);
  }, []);
  const priorScopeRef = useRef(`${effectiveUserId}:${currentOrganization?.id || ''}`);
  useEffect(() => {
    const scope = `${effectiveUserId}:${currentOrganization?.id || ''}`;
    if (priorScopeRef.current !== scope) {
      priorScopeRef.current = scope;
      resetDraft(); setMessages([]); setCurrentConversationId(null); setDraft(null);
    }
  }, [effectiveUserId, currentOrganization?.id, resetDraft]);
  useEffect(() => () => { generationRef.current += 1; inFlightAbortRef.current?.abort(); }, []);
  const stoppedByUserRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  
  // Navigation handler
  const handleNavigation = useCallback((path: string, anchor?: string) => {
    if (!path) return;
    
    logger.info('Executing chatbot navigation to:', path);
    
    // Check if we're in mobile WebView
    if (isMobileWebView) {
      // Send navigation request to native app (route-only — the WebView bridge
      // exchanges route strings; anchors are a web-dashboard feature, #2171)
      if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'navigate',
          path
        }));
      }
    } else {
      // Standard web navigation
      router.push(path);
      // Scroll to and spotlight the anchored section once it mounts (#2171).
      // Degrades silently to plain navigation when the anchor never appears.
      if (anchor) {
        scrollToAssistantAnchor(anchor);
      }
    }
    
    // Auto-close chat on mobile after navigation to show the new page
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsOpen(false);
    }
  }, [router, isMobileWebView]);
  
  // Persist state to sessionStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('chat_is_open', isOpen.toString());
    }
    // Closing the assistant lets go of a question that was never sent.
    if (!isOpen) setDraft(null);
  }, [isOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (currentConversationId) {
        sessionStorage.setItem('chat_current_conversation_id', currentConversationId);
      } else {
        sessionStorage.removeItem('chat_current_conversation_id');
      }
    }
  }, [currentConversationId]);

  // Ref to track if we've initialized
  const initializedRef = useRef(false);
  
  // Ref to store loadConversation function to avoid initialization order issues
  const loadConversationRef = useRef<((id: string) => Promise<void>) | null>(null);
  
  // Generate unique message ID
  const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  /**
   * The gym every assistant request is scoped to.
   *
   * `OrganizationContext` is the authority. The remembered gym
   * (`utils/organizations/rememberedOrganization`) is a bootstrap for the window
   * before it resolves — the mobile WebView needs it, and so does a demo-mode
   * switch — but it is not membership-checked at write time and it outlives a
   * change of signed-in user in the same browser. #4571: a CheerVue "Sign in as"
   * support session landed on /dashboard still holding the PREVIOUS gym's id,
   * and this hook's mount-time burst sent it, earning a 403 from the server org
   * guard.
   *
   * Three cases, in order, and the order is the point — only the first is
   * positive evidence, so everything below it is treated as unproven:
   *
   *  1. Stamped with the user asking. Trusted immediately, loading or not: the
   *     writer knew who was signed in, and it was them.
   *  2. Stamped with somebody else. Discarded and forgotten — the
   *     support-session case.
   *  3. Stamped, but we do not know who is asking (`effectiveUserId` is empty
   *     during a mobile handoff exchange and during the AuthContext-loading
   *     window). Refused outright, loading or not. The stamp proves nothing
   *     with no user to compare it to, and "not provably someone else's" is
   *     not "mine" — reading it as such is how an ownership rule fails open.
   *  4. No stamp recorded at all. Used only once the context has finished
   *     loading, because nothing distinguishes it from a stale value before
   *     that. This is the case every browser in the field is in until its
   *     owner's next OrganizationContext resolve stamps it.
   *
   * Nothing here adopts or re-stamps a value. Stamping belongs to whoever knows
   * the gym is the user's — OrganizationContext does it on every resolve — and
   * stamping from the reader would launder an unproven value into a permanently
   * trusted one.
   *
   * The stamp is advisory. The server org guard remains the only thing that
   * decides what a caller may read.
   */
  const resolveOrganizationId = useCallback((): string | null => {
    if (currentOrganization?.id) return currentOrganization.id;

    const remembered = readRememberedOrganization(effectiveUserId);
    if (!remembered) return null;

    if (remembered.ownedByCurrentUser) return remembered.organizationId;

    if (remembered.ownedBySomeoneElse) {
      // Drop it so no later caller reads it either — including the sibling gym
      // list, which was that other user's too.
      forgetRememberedOrganization({ includeOrganizationList: true });
      return null;
    }

    // Only an UNSTAMPED value may pass on timing. A stamped one read by an
    // unknown user (`effectiveUserId` empty) proves nothing either way, and
    // letting it through once loading finished was the doc comment's rule
    // implemented one notch weaker — the same fail-open shape as round 2's.
    if (isOrganizationLoading || !remembered.ownerUnknown) return null;
    return remembered.organizationId;
  }, [currentOrganization?.id, isOrganizationLoading, effectiveUserId]);

  // API helper — always fetches a fresh token to avoid stale JWT after idle
  const apiCall = useCallback(async (
    endpoint: string,
    options: RequestInit = {}
  ) => {
    const accessToken = await getFreshAccessToken(mobileAuthToken) || effectiveAccessToken;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    
    const orgId = resolveOrganizationId();
    
    if (!orgId) {
      throw new Error(ORGANIZATION_NOT_READY_MESSAGE);
    }
    
    // Cross-org queries for parents with athletes at several gyms. Safe to read
    // raw: this line is only reached once resolveOrganizationId() returned an
    // id, and the one path that rejects a remembered gym forgets this list with
    // it (#4571).
    const allOrgIds = typeof window !== 'undefined' 
      ? localStorage.getItem(REMEMBERED_ALL_ORGS_KEY) 
      : null;
    
    // 🔥 OPTIMIZATION: Send Bearer token if available.
    // This provides a robust fallback for server-side auth if cookies are missing.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-organization-id': orgId,
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    if (allOrgIds) {
      headers['x-all-organization-ids'] = allOrgIds;
    }
    
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Owned copy for a 401 (#2839), matching features/team-chat/chatApi.ts
      // and the phone app. This hook backs the WebView chat surface, so a
      // divergence here shows up on a phone too.
      throw new Error(
        apiErrorCopy(response.status, errorData.error, `Request failed: ${response.status}`)
      );
    }
    
    return response.json();
  }, [effectiveAccessToken, mobileAuthToken, resolveOrganizationId]);
  
  // Fetch conversation history
  // NOTE: The API returns ALL conversations for the user, regardless of current organization
  // This ensures users see their full chat history even when switching between gyms
  const refreshConversations = useCallback(async () => {
    if (!effectiveUserId) return;
    
    // We still need SOME org ID for the API header, but conversations aren't filtered by it
    const orgId = resolveOrganizationId();
    
    if (!orgId) {
      logger.warn('No organization ID available for API call');
      return;
    }
    
    const generation = generationRef.current;
    try {
      setIsLoading(true);
      const data = await apiCall('/api/assistant/conversations?limit=10');
      if (generation !== generationRef.current) return;
      const loadedConversations = data.conversations || [];
      setConversations(loadedConversations);
      logger.info('Loaded conversations:', { count: loadedConversations.length });

      // If we have a currentConversationId but no messages, try to load it
      // But first check if it exists in the loaded list to avoid unnecessary API calls
      if (currentConversationId && messages.length === 0) {
        const conversationExists = loadedConversations.some(c => c.id === currentConversationId);
        if (conversationExists && loadConversationRef.current) {
          loadConversationRef.current(currentConversationId);
        } else if (!conversationExists) {
          // Conversation not in list - it was likely deleted/archived
          // Clear the stale ID silently
          logger.info('Stale conversation ID not found in list, clearing:', currentConversationId);
          setCurrentConversationId(null);
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('chat_current_conversation_id');
          }
        }
      }
    } catch (err: any) {
      logger.warn('Failed to fetch conversations:', err.message);
      // Don't set error - this is non-critical
    } finally {
      if (generation === generationRef.current) setIsLoading(false);
    }
  }, [effectiveUserId, resolveOrganizationId, apiCall, currentConversationId, messages.length]);

  // Fetch smart suggestions
  const refreshSuggestions = useCallback(async () => {
    if (!effectiveUserId) return;

    // Check for organization ID from context or localStorage
    const orgId = resolveOrganizationId();
    
    if (!orgId) return;

    try {
      // Include current page in suggestions request for page-aware suggestions
      const suggestionsUrl = `/api/assistant/suggestions${pathname ? `?current_page=${encodeURIComponent(pathname)}` : ''}`;
      const data = await apiCall(suggestionsUrl);
      if (data.success && data.suggestions) {
        setSuggestions(data.suggestions);
      }
    } catch (err: any) {
      logger.warn('Failed to fetch suggestions:', err.message);
    }
  }, [effectiveUserId, resolveOrganizationId, apiCall, pathname]);
  
  // Load a specific conversation
  const loadConversation = useCallback(async (conversationId: string) => {
    resetDraft();
    const generation = generationRef.current;
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await apiCall(`/api/assistant/conversations/${conversationId}`);
      if (generation !== generationRef.current) return;
      const conversation = data.conversation;
      const analytics: Array<{ id: string; user_message: string; user_feedback: string | null }> = data.analytics || [];
      
      if (conversation) {
        setCurrentConversationId(conversation.id);
        
        // Build a map of analytics records keyed by user_message content
        // so we can match them to the assistant response that follows each user message
        const analyticsQueue = [...analytics];
        
        const loadedMessages = (conversation.messages || []).map((msg: any, index: number) => {
          const base: ChatMessage = {
            id: msg.id || `loaded_${index}`,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || conversation.created_at,
            attachments: msg.attachments,
            supportAttachmentStatus: msg.supportAttachmentStatus,
          };
          
          // For assistant messages, try to find a matching analytics record
          // by checking if the previous message (user) matches an analytics user_message
          if (msg.role === 'assistant' && analyticsQueue.length > 0) {
            const prevMsg = conversation.messages?.[index - 1];
            const matchIdx = prevMsg 
              ? analyticsQueue.findIndex((a: any) => a.user_message === prevMsg.content)
              : -1;
            
            if (matchIdx !== -1) {
              const matched = analyticsQueue.splice(matchIdx, 1)[0];
              base.analyticsId = matched.id;
              if (matched.user_feedback) {
                base.feedback = matched.user_feedback as 'positive' | 'negative';
              }
            }
          }
          
          return base;
        });
        
        if (generation === generationRef.current) setMessages(loadedMessages);
      }
    } catch (err: any) {
      if (generation !== generationRef.current) return;
      // Handle "Conversation not found" gracefully - clear stale ID and start fresh
      const errorMessage = err.message || '';
      const isNotFound = errorMessage.includes('Conversation not found') || 
                         errorMessage.includes('404') ||
                         errorMessage.includes('Request failed: 404');
      
      if (isNotFound) {
        // Conversation was deleted/archived - clear stale ID silently
        logger.info('Conversation not found, clearing stale ID:', conversationId);
        setCurrentConversationId(null);
        setMessages([]);
        
        // Clear from sessionStorage
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('chat_current_conversation_id');
        }
        
        // Don't show error - this is expected when conversations are deleted
      } else {
        // Real error - show it to the user
        logger.error('Failed to load conversation:', err);
        setError('Failed to load conversation');
      }
    } finally {
      if (generation === generationRef.current) setIsLoading(false);
    }
  }, [apiCall, resetDraft]);
  
  // Store loadConversation in ref so refreshConversations can call it safely
  useEffect(() => {
    loadConversationRef.current = loadConversation;
  }, [loadConversation]);
  
  // Start a new conversation
  const startNewConversation = useCallback(() => {
    resetDraft();
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    logger.info('Started new conversation');
  }, [resetDraft]);
  
  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      await apiCall(`/api/assistant/conversations?id=${conversationId}`, {
        method: 'DELETE',
      });
      
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      if (currentConversationId === conversationId) {
        startNewConversation();
      }
      
      logger.info('Conversation deleted:', conversationId);
    } catch (err: any) {
      logger.error('Failed to delete conversation:', err);
      setError('Failed to delete conversation');
    }
  }, [apiCall, currentConversationId, startNewConversation]);
  
  const loadAttachment = useCallback(async (id: string) => {
    const token = await getFreshAccessToken(mobileAuthToken) || effectiveAccessToken;
    const org = resolveOrganizationId();
    if (!token || !org) throw new Error('Please sign in again to view this screenshot.');
    const response = await fetch(`/api/assistant/attachments/${encodeURIComponent(id)}`, { headers: { Authorization: `Bearer ${token}`, 'x-organization-id': org } });
    if (!response.ok) throw new Error('This screenshot could not be loaded.');
    return response.blob();
  }, [mobileAuthToken, effectiveAccessToken, resolveOrganizationId]);

  // Send a message with streaming support
  // quickCommand: Optional string indicating which quick command button was clicked
  const sendMessage = useCallback(async (message: string, quickCommand?: string, files: File[] = []) => {
    if ((!message.trim() && !files.length) || isSending || isLoading || sendLockRef.current) return false;
    if (files.length > 3) { setError('You can attach up to 3 screenshots per message.'); return false; }
    sendLockRef.current = true;
    const generation = generationRef.current;
    let sendConversationId = currentConversationId;
    let completed = false;
    message = message.trim() || 'Please help me with this screenshot.';
    
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString(),
    };
    
    const assistantMessageId = generateId();
    const streamingMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isLoading: true,
    };
    
    setMessages(prev => [...prev, userMessage, streamingMessage]);
    setIsSending(true);
    setError(null);

    // Declared outside the try so the catch and finally can see them.
    // The server never sends its opening greeting ("Hi Geng! ") on its own —
    // it rides in front of the first answer text — so any text event means
    // there is an answer to show.
    let accumulatedContent = '';
    let hasAnswerText = false;
    let overallTimer: ReturnType<typeof setTimeout> | null = null;
    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    
    try {
      // Created first so Stop already works while the token is being fetched.
      const abortController = new AbortController();
      inFlightAbortRef.current = abortController;
      stoppedByUserRef.current = false;

      // Always get a fresh token — prevents stale JWT errors after idle periods
      const accessToken = await getFreshAccessToken(mobileAuthToken) || effectiveAccessToken;
      if (!accessToken) {
        throw new Error('Not authenticated');
      }
      
      const orgId = resolveOrganizationId();
      
      if (!orgId) {
        throw new Error(ORGANIZATION_NOT_READY_MESSAGE);
      }
      
      const attachments: AssistantAttachment[] = [];
      for (const file of files) {
        let uploaded = uploadedRef.current.get(file);
        if (!uploaded) {
          try {
            const prepared = await prepareScreenshot(file);
            if (generation !== generationRef.current || abortController.signal.aborted) throw new DOMException('Aborted', 'AbortError');
            const body = new FormData(); body.append('file', prepared);
            if (sendConversationId) body.append('conversation_id', sendConversationId);
            const upload = await fetch('/api/assistant/attachments', {
              method: 'POST', body, signal: abortController.signal,
              headers: { Authorization: `Bearer ${accessToken}`, 'x-organization-id': orgId },
            });
            const result = await upload.json();
            if (!upload.ok) throw new Error(result.error || 'Could not upload screenshot.');
            if (generation !== generationRef.current) throw new DOMException('Aborted', 'AbortError');
            uploaded = { attachment: result.attachment, conversationId: result.conversation_id };
            uploadedRef.current.set(file, uploaded);
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') throw error;
            throw new Error(`${file.name}: ${error instanceof Error ? error.message : 'Could not upload. Please try again.'}`);
          }
        }
        sendConversationId = uploaded.conversationId;
        attachments.push(uploaded.attachment);
      }
      if (generation !== generationRef.current || abortController.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      if (sendConversationId && sendConversationId !== currentConversationId) setCurrentConversationId(sendConversationId);
      if (attachments.length) setMessages(previous => previous.map(item => item.id === userMessage.id ? { ...item, attachments } : item));
      const allOrgIds = typeof window !== 'undefined' 
        ? localStorage.getItem(REMEMBERED_ALL_ORGS_KEY) 
        : null;
      
      // Build conversation history (last 10 messages for context)
      const history = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content,
      }));
      
      // Get page title for additional context (optional)
      const pageTitle = typeof window !== 'undefined' ? document.title : '';
      
      // 🔥 OPTIMIZATION: Send Bearer token if available.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-organization-id': orgId,
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      if (allOrgIds) {
        headers['x-all-organization-ids'] = allOrgIds;
      }

      // Use streaming endpoint with abort controller for timeout protection
      // Prevents "Thinking..." from being stuck forever if the connection drops
      // Must exceed the route's maxDuration (300s) or the client gives up
      // while the server is still legitimately working.
      const OVERALL_TIMEOUT_MS = 310_000;
      const INACTIVITY_TIMEOUT_MS = 30_000; // 30s without any data = stale connection
      overallTimer = setTimeout(() => {
        logger.warn(`Streaming overall timeout reached (${OVERALL_TIMEOUT_MS / 1000}s)`);
        abortController.abort();
      }, OVERALL_TIMEOUT_MS);

      const response = await fetch('/api/assistant/stream', {
        method: 'POST',
        headers,
        signal: abortController.signal,
        body: JSON.stringify({
          message: message.trim(),
          conversation_id: sendConversationId,
          attachment_ids: attachments.map(attachment => attachment.id),
          conversation_history: history,
          current_page: pathname || '', // Include current page path
          page_title: pageTitle, // Include page title for context
          quick_command_used: quickCommand || null, // Track which quick command was clicked
          client_platform: isMobileWebView ? 'mobile' : 'web', // Which surface the user is chatting from
        }),
      });
      
      if (!response.ok) {
        // The streaming branch, 280 lines from fetchApi's and easy to miss —
        // review caught it. This message is rendered verbatim in the assistant's
        // red banner (ChatWindow.tsx), so on a stale sign-in it literally said
        // "Request failed: 401". No server `error` field to forward here, hence
        // the null; apiErrorCopy still owns the 401 (#2839).
        throw new Error(apiErrorCopy(response.status, null, `Request failed: ${response.status}`));
      }
      
      if (!response.body) {
        throw new Error('No response body');
      }
      
      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      accumulatedContent = '';
      hasAnswerText = false;
      let toolsUsed: string[] = [];
      let newConversationId: string | null = null;
      let buffer = '';
      let receivedDoneEvent = false;

      // Inactivity timer: abort if no data received for 30 seconds
      // (e.g., connection half-open, Vercel function stalled)
      const resetInactivityTimer = () => {
        if (inactivityTimer) clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => {
          logger.warn('Streaming inactivity timeout (30s without data)');
          abortController.abort();
        }, INACTIVITY_TIMEOUT_MS);
      };
      resetInactivityTimer();

      while (true) {
        const { done, value } = await reader.read();
        if (generation !== generationRef.current) throw new DOMException('Aborted', 'AbortError');
        if (done) break;
        resetInactivityTimer();
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        let currentEvent = '';
        let currentData = '';
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
            
            if (currentEvent && currentData) {
              try {
                const data = JSON.parse(currentData);
                
                switch (currentEvent) {
                  case 'text':
                    // Append streaming text
                    accumulatedContent += data.content;
                    hasAnswerText = true;
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: accumulatedContent, isLoading: false }
                          : msg
                      )
                    );
                    break;
                    
                  case 'support_attachment_status':
                    setMessages(previous => previous.map(item => item.id === assistantMessageId ? { ...item, supportAttachmentStatus: data } : item));
                    break;
                  case 'tool_start':
                    // Show tool execution status
                    logger.info('Tools executing:', data.tools);
                    break;
                    
                  case 'tool_end':
                    // Tools finished
                    logger.info('Tools completed:', data.tools);
                    break;

                  case 'data_mutated':
                    // Chatbot mutated data — dispatch a DOM event so the
                    // affected page can re-fetch its list automatically.
                    // Follows the same CustomEvent pattern used by
                    // 'announcement-read' and 'brandThemeChange'.
                    if (typeof window !== 'undefined' && data.entity) {
                      window.dispatchEvent(
                        new CustomEvent('chatbot-data-mutated', {
                          detail: { entity: data.entity, tool: data.tool },
                        })
                      );
                      logger.info('Chatbot data mutation:', data.entity);
                    }
                    break;

                  case 'navigate':
                    // Handle server-triggered navigation - now with confirmation
                    if (data.path) {
                      setMessages(prev => 
                        prev.map(msg => 
                          msg.id === assistantMessageId 
                            ? { 
                                ...msg, 
                                pendingNavigation: { 
                                  path: data.path, 
                                  reason: data.reason,
                                  anchor: data.anchor
                                } 
                              }
                            : msg
                        )
                      );
                    }
                    break;
                    
                  case 'done':
                    // Streaming complete
                    receivedDoneEvent = true;
                    completed = true;
                    files.forEach(file => uploadedRef.current.delete(file));
                    toolsUsed = data.tools_used || [];
                    newConversationId = data.conversation_id;
                    // Commit the id here: the server keeps the stream open after
                    // done for analytics, so a Stop pressed in that window must
                    // not lose a brand-new conversation's id.
                    if (newConversationId && !currentConversationId) {
                      setCurrentConversationId(newConversationId);
                    }
                    const analyticsId = data.analytics_id;
                    
                    // Update message with final data including analytics_id for feedback
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: accumulatedContent, toolsUsed, isLoading: false, analyticsId }
                          : msg
                      )
                    );
                    break;
                    
                  case 'error':
                    throw new Error(data.message || 'Streaming error');
                }
              } catch (parseError) {
                // Ignore JSON parse errors for incomplete data
                if (currentEvent === 'error') {
                  throw parseError;
                }
              }
              
              currentEvent = '';
              currentData = '';
            }
          }
        }
      }
      
      // Handle premature stream end: if we got answer text but no 'done' event,
      // the connection was likely dropped (Vercel timeout, network issue).
      // Show whatever content we accumulated instead of leaving "Thinking..." stuck.
      if (!receivedDoneEvent && hasAnswerText) {
        logger.warn('Stream ended without done event — showing partial response', {
          contentLength: accumulatedContent.length,
        });
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, content: accumulatedContent, isLoading: false }
              : msg
          )
        );
      } else if (!receivedDoneEvent) {
        // Stream closed before any answer text arrived (e.g. the function was
        // killed mid tool round, or right after the greeting). Nothing to show,
        // so drop the placeholder bubble — otherwise it stays "Thinking..."
        // until the page reloads.
        logger.warn('Stream ended without done event and without content');
        setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
        setError(STREAM_TIMEOUT_MESSAGE);
      }

      // Update conversation ID if new
      if (newConversationId && !currentConversationId) {
        setCurrentConversationId(newConversationId);
      }

      completed = receivedDoneEvent;
      if (receivedDoneEvent) {
        logger.info('Streaming message sent successfully', {
          conversationId: newConversationId || currentConversationId,
          toolsUsed,
          responseLength: accumulatedContent.length,
        });
      }
      
    } catch (err: any) {
      if (generation !== generationRef.current) return false;
      // Handle abort/timeout errors gracefully — show partial content if available
      const isAbort = err.name === 'AbortError';
      const stoppedByUser = stoppedByUserRef.current;
      if (isAbort && stoppedByUser) {
        logger.info('Streaming stopped by user');
      } else if (isAbort) {
        logger.warn('Streaming request aborted (timeout)');
      } else {
        logger.error('Failed to send message:', err);
      }

      // If answer text arrived before the error, show it instead of removing the message
      const hadPartialContent = hasAnswerText;
      setMessages(prev => {
        const streamingMsg = prev.find(msg => msg.id === assistantMessageId);
        if (streamingMsg && hadPartialContent) {
          // Keep partial response visible
          return prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, isLoading: false }
              : msg
          );
        }
        // No content accumulated — remove the empty message and show error
        return prev.filter(msg => msg.id !== assistantMessageId);
      });

      if (!isAbort) {
        setError(err.message || 'Failed to send message');
      } else if (!hadPartialContent && !stoppedByUser) {
        setError(STREAM_TIMEOUT_MESSAGE);
      }

    } finally {
      if (overallTimer) clearTimeout(overallTimer);
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inFlightAbortRef.current = null;
      setIsSending(false);
      sendLockRef.current = false;
      if (files.length && !completed && !hasAnswerText && generation === generationRef.current) setMessages(previous => previous.filter(item => item.id !== userMessage.id));
    }
    return completed;
  }, [messages, currentConversationId, isSending, isLoading, effectiveAccessToken, resolveOrganizationId, pathname, isMobileWebView, mobileAuthToken]);
  
  const retrySupportAttachments = useCallback(async (messageId: string) => {
    const status = messages.find(message => message.id === messageId)?.supportAttachmentStatus;
    if (!status || !currentConversationId) return;
    const generation = generationRef.current;
    try {
      const response = await apiCall('/api/assistant/attachments/transfer', {
        method: 'POST', body: JSON.stringify({ conversation_id: currentConversationId, request_id: status.request_id, attachment_ids: status.retry_attachment_ids ?? status.failed.map(file => file.id) }),
      });
      const result = response.data || response;
      if (generation === generationRef.current) setMessages(previous => previous.map(message => message.id === messageId ? { ...message, supportAttachmentStatus: result } : message));
    } catch (error) {
      if (generation === generationRef.current) setError(error instanceof Error ? error.message : 'Could not attach screenshots. Please try again.');
    }
  }, [messages, currentConversationId, apiCall]);

  // Toggle chat open/closed
  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  
  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Stop the streaming answer. The catch block in sendMessage keeps whatever
  // text already arrived and shows no error line for a user-chosen stop.
  const stopGeneration = useCallback(() => {
    const controller = inFlightAbortRef.current;
    if (!controller) return;
    stoppedByUserRef.current = true;
    controller.abort();
  }, []);

  // Submit feedback for a message
  // For negative feedback, an optional comment can describe what went wrong
  const submitFeedback = useCallback(async (messageId: string, feedback: 'positive' | 'negative', comment?: string) => {
    // Find the message
    const message = messages.find(m => m.id === messageId);
    if (!message?.analyticsId) {
      logger.warn('Cannot submit feedback: no analytics_id for message', { messageId });
      return;
    }

    // Optimistically update the UI
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId ? { ...msg, feedback } : msg
      )
    );

    try {
      const response = await apiCall('/api/assistant/feedback', {
        method: 'POST',
        body: JSON.stringify({
          analytics_id: message.analyticsId,
          feedback,
          // Include comment for negative feedback (allows user to describe the issue)
          ...(comment && { feedback_comment: comment }),
        }),
      });

      if (!response.success) {
        // Revert on failure
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId ? { ...msg, feedback: undefined } : msg
          )
        );
        logger.error('Failed to submit feedback:', response.error);
      } else {
        logger.info('Feedback submitted successfully', { messageId, feedback, hasComment: !!comment });
      }
    } catch (err: any) {
      // Revert on error
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, feedback: undefined } : msg
        )
      );
      logger.error('Error submitting feedback:', err.message);
    }
  }, [messages, apiCall]);
  
  // Confirm or decline a pending navigation
  const confirmNavigation = useCallback((messageId: string, confirm: boolean) => {
    const message = messages.find(m => m.id === messageId);
    if (!message?.pendingNavigation) return;

    if (confirm) {
      handleNavigation(message.pendingNavigation.path, message.pendingNavigation.anchor);
    }

    // Clear the pending navigation from the message
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId ? { ...msg, pendingNavigation: undefined } : msg
      )
    );
  }, [messages, handleNavigation]);
  
  // Load conversations on mount. The org comes from the same resolver every
  // other call uses, so the mount-time burst cannot fire on a gym the context
  // has not confirmed (#4571).
  useEffect(() => {
    const hasOrgId = !!resolveOrganizationId();
    
    if (autoLoadHistory && effectiveUserId && hasOrgId && !initializedRef.current) {
      initializedRef.current = true;
      refreshConversations();
      refreshSuggestions();
    }
  }, [autoLoadHistory, effectiveUserId, resolveOrganizationId, refreshConversations, refreshSuggestions]);
  
  return {
    // State
    draftKey,
    draft,
    prefillDraft,
    clearDraft,
    loadAttachment,
    retrySupportAttachments,
    messages,
    conversations,
    currentConversationId,
    suggestions,
    isOpen,
    isLoading,
    isSending,
    error,
    
    // Actions
    sendMessage,
    startNewConversation,
    loadConversation,
    deleteConversation,
    setIsOpen,
    toggleOpen,
    clearError,
    stopGeneration,
    refreshConversations,
    refreshSuggestions,
    submitFeedback,
    confirmNavigation,
  };
}
